import React, { type FunctionComponent, useState, useCallback, useEffect } from 'react'
import ReactSelect, { type Styles } from 'react-select'
import { useTheme } from '@instacart/ids-core'
import { Modal, ModalHeader } from '@retailer-platform/shared-components'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'
import {
  type GroupedOptions,
  type OnRetailerLocationStoreGroupChange,
  type RetailerLocationStoreGroup,
} from 'domains/account-management/components/retailer-location-store-group-select/utils/types'
import { Group } from './Group'
import { Option } from './Option'
import { DropdownIndicator } from './DropdownIndicator'
import { useSelectStyles, modalStyles, StyledModalFooter, StyledButton } from './SelectModal.styles'

interface SelectModalProps {
  loading: boolean
  open: boolean
  value: RetailerLocationStoreGroup
  onChange: OnRetailerLocationStoreGroupChange
  onClose: () => void
  options: GroupedOptions[]
  error: Error
}

export const SelectModal: FunctionComponent<SelectModalProps> = ({
  loading,
  open,
  value,
  options,
  onChange,
  onClose,
  error,
}) => {
  const [selection, setSelection] = useState<GroupedOptions>(value as unknown as GroupedOptions)
  const theme = useTheme()
  const selectStyles: Styles = useSelectStyles({ theme })

  useEffect(() => {
    setSelection(value as unknown as GroupedOptions)
  }, [value])

  const messages = useDomainMessages({
    modalTitle: 'accountManagementDomain.form.addStore.modalTitle',
    search: 'accountManagementDomain.form.addStore.search',
    clearAllSelections: 'accountManagementDomain.form.addStore.clearAllSelections',
    save: 'accountManagementDomain.form.addStore.save',
    close: 'accountManagementDomain.form.addStore.close',
  })

  const handleSave = useCallback(() => {
    onChange(selection as unknown as GroupedOptions['options'])
    onClose()
  }, [onChange, onClose, selection])

  const handleClear = useCallback(() => {
    // @ts-ignore react-select's typing is wack and the value/selection is actually an array of values
    // not GroupedOptions, but it requires the GroupedOptions type and applies it everywhere
    setSelection([])
  }, [])

  return (
    <Modal isOpen={open} onRequestClose={onClose} styleOverrides={modalStyles}>
      <ModalHeader>{messages.modalTitle}</ModalHeader>
      <ReactSelect
        placeholder={messages.search}
        controlShouldRenderValue={false}
        isMulti
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        isSearchable={true}
        isClearable={false}
        loading={loading}
        options={options}
        styles={selectStyles}
        components={{ DropdownIndicator, Group, Option }}
        error={error}
        onChange={setSelection}
        emptyValue={undefined}
        value={selection}
        menuIsOpen
      />
      <StyledModalFooter>
        <StyledButton variant="tertiary" onClick={handleClear} type="button">
          {messages.clearAllSelections}
        </StyledButton>
        <StyledButton variant="primary" onClick={handleSave} type="button">
          {messages.save}
        </StyledButton>
      </StyledModalFooter>
    </Modal>
  )
}
